import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { SubscriptionCancelPanel, type SubscriptionCancelChoice } from '../../components/SubscriptionCancelPanel';
import { formatAdminAccountMoney } from '../../types/account';
import type { UgotProAdminOrder, UgotProAdminSubscription, UgotProBilling, UgotProRefundRequest } from '../../ugotPro/ugotProTypes';
import {
  UGOT_PRO_REFUND_STATUS_LABELS,
  canRefundUgotProOrder,
  formatUgotProDateTime,
  isUgotProRefundInProgress,
  latestRefundByOrder,
  ugotProCancelOptions,
} from '../../ugotPro/ugotProFormat';
import { UgotProRefundEvidence } from './UgotProRefundEvidence';

type Props = {
  billing: UgotProBilling;
  busyKey: string | null;
  onCancel: (subscription: UgotProAdminSubscription, choice: SubscriptionCancelChoice) => void | Promise<void>;
  onRefund: (order: UgotProAdminOrder, reason: string) => void | Promise<void>;
};

function RefundOrderControl({ order, busy, onRefund }: { order: UgotProAdminOrder; busy: boolean; onRefund: Props['onRefund'] }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`ugot-pro-refund-open-${order.orderId}`}>
        돈 돌려주기(환불)…
      </Button>
    );
  }
  return (
    <div className="w-full space-y-2 rounded-md border border-red-200 bg-red-50/60 p-3">
      <p className="text-sm text-red-900">
        가장 최근에 결제된 거래({formatAdminAccountMoney(order.amount, order.currency)})를 Paddle 에 <b>전액 환불</b> 요청합니다.
        주문이 REFUNDED 로 바뀌고 구독이 끝나는 것은 Paddle 웹훅이 확정합니다.
      </p>
      <label className="block space-y-1 text-sm">
        <span className="text-muted-foreground">환불 사유(필수)</span>
        <Input value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} data-testid={`ugot-pro-refund-reason-${order.orderId}`} />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="destructive"
          size="sm"
          disabled={busy || !reason.trim()}
          onClick={() => void onRefund(order, reason.trim())}
          data-testid={`ugot-pro-refund-confirm-${order.orderId}`}
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          전액 환불 요청
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setOpen(false)}>닫기</Button>
      </div>
    </div>
  );
}

function RefundStatusBadge({ refund }: { refund: UgotProRefundRequest }) {
  const label = UGOT_PRO_REFUND_STATUS_LABELS[refund.status];
  return <Badge variant={label.tone}>{isUgotProRefundInProgress(refund.status) ? `환불 처리 중 · ${label.label}` : `환불 ${label.label}`}</Badge>;
}

/** 결제 구독(해지)·주문(환불)·환불 요청 기록. 해지와 환불은 서로 다른 버튼이다. */
export function UgotProBillingSection({ billing, busyKey, onCancel, onRefund }: Props) {
  const latest = latestRefundByOrder(billing.refundRequests);
  return (
    <div className="space-y-4">
      <section className="space-y-3 rounded-lg border bg-card p-4" data-testid="ugot-pro-subscriptions">
        <h3 className="text-sm font-semibold tracking-tight">결제 구독(Paddle)</h3>
        {billing.subscriptions.length === 0 && <p className="text-sm text-muted-foreground">UGOT Pro 결제 구독이 없습니다.</p>}
        {billing.subscriptions.map((subscription) => {
          const options = ugotProCancelOptions(subscription);
          const orderRefund = subscription.orderId ? latest.get(subscription.orderId) : undefined;
          return (
            <div key={subscription.subscriptionId} className="space-y-2 rounded-lg border bg-background p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={subscription.status === 'ACTIVE' ? 'success' : 'outline'}>{subscription.status}</Badge>
                <span className="font-medium">{subscription.planCode || 'UGOT Pro'}</span>
                {orderRefund && isUgotProRefundInProgress(orderRefund.status) && (
                  <Badge variant="warning">환불 처리 중 — 승인되면 구독 종료</Badge>
                )}
              </div>
              <div className="grid gap-x-4 gap-y-1 text-sm text-muted-foreground sm:grid-cols-2">
                <div>시작: {formatUgotProDateTime(subscription.startDate)}</div>
                <div>다음 결제: {formatUgotProDateTime(subscription.nextBillingDate)}</div>
                <div>종료(예정): {formatUgotProDateTime(subscription.endDate)}</div>
                <div>자동 갱신: {subscription.autoRenewing === null ? '-' : subscription.autoRenewing ? '켜짐' : '꺼짐'}</div>
                <div className="break-all">Paddle: {subscription.paddleSubscriptionId || '-'}</div>
                <div className="break-all">최근 거래: {subscription.latestTransactionId || '-'}</div>
              </div>
              <SubscriptionCancelPanel
                allowPeriodEnd={options.periodEnd}
                allowImmediate={options.immediate}
                scheduledEndLabel={options.scheduledEnd ? formatUgotProDateTime(options.scheduledEnd) : null}
                defaultReason="관리자 확인 후 해지"
                busy={busyKey === `cancel:${subscription.subscriptionId}`}
                onConfirm={(choice) => onCancel(subscription, choice)}
                testId={`ugot-pro-cancel-${subscription.subscriptionId}`}
              />
            </div>
          );
        })}
      </section>

      <section className="space-y-3 rounded-lg border bg-card p-4" data-testid="ugot-pro-orders">
        <h3 className="text-sm font-semibold tracking-tight">주문</h3>
        {billing.orders.length === 0 && <p className="text-sm text-muted-foreground">UGOT Pro 주문이 없습니다.</p>}
        {billing.orders.map((order) => {
          const refund = latest.get(order.orderId);
          return (
            <div key={order.orderId} className="space-y-2 rounded-lg border bg-background p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={order.status === 'REFUNDED' ? 'outline' : 'success'}>{order.status}</Badge>
                <span className="font-medium">{formatAdminAccountMoney(order.amount, order.currency)}</span>
                <span className="text-sm text-muted-foreground">{order.planCode || ''}</span>
                {refund && <RefundStatusBadge refund={refund} />}
              </div>
              <div className="grid gap-x-4 gap-y-1 text-sm text-muted-foreground sm:grid-cols-2">
                <div className="break-all">주문: {order.orderId}</div>
                <div>결제: {formatUgotProDateTime(order.paymentDate)}</div>
                <div className="break-all">첫 거래: {order.transactionId || '-'}</div>
                <div>수단: {order.paymentMethod || '-'}</div>
              </div>
              {canRefundUgotProOrder(order, refund) && (
                <RefundOrderControl order={order} busy={busyKey === `refund:${order.orderId}`} onRefund={onRefund} />
              )}
            </div>
          );
        })}
      </section>

      {billing.refundRequests.length > 0 && (
        <section className="space-y-3 rounded-lg border bg-card p-4" data-testid="ugot-pro-refund-history">
          <h3 className="text-sm font-semibold tracking-tight">환불 요청 기록</h3>
          {billing.refundRequests.map((refund) => (
            <div key={refund.refundRequestId} className="space-y-1 rounded-lg border bg-background p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <RefundStatusBadge refund={refund} />
                <span className="text-muted-foreground">#{refund.refundRequestId} · {formatUgotProDateTime(refund.createdAt)}</span>
              </div>
              <UgotProRefundEvidence refund={refund} />
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
